import useAgency from 'features/Agency/useAgency';
import ChargeHelper, { ChargeConstants } from 'features/Charge/chargeHelper';
import CardVehicleView from 'features/Charge/ChargeVehicle/components/CardVehicleView';
import DialogAddVehicleView from 'features/Charge/ChargeVehicle/components/DialogAddVehicleView';
import { AnimatePresence, motion } from 'framer-motion';
import { AppCustomDialogHelper } from 'general/components/AppDialogCustom';
import AppTabs from 'general/components/AppTabs';
import KTLabel, { KTLabelSize } from 'general/components/OtherKeenComponents/KTLabel';
import WalletCard from 'general/components/quybka/Cards/WalletCard';
import AppColors from 'general/constants/AppColors';
import AppResource from 'general/constants/AppResource';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import useRouter from 'hooks/useRouter';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import DriverPaymentHistoryView from '../DriverPaymentHistoryView';
import DriverTopUpHistoryView from '../DriverTopUpHistoryView';
import AppConstants from 'general/constants/AppConstants';
import FleetHelper, { FleetConstants } from 'features/Fleet/fleetHelper';
import KTBSButton, { KTBSButtonColor, KTBSButtonSize } from 'general/components/OtherKeenComponents/KTBSButton';
import { useEkyc } from 'hooks/useEKYC';
import DealerHelper from 'features/ManageDealer/dealerHelper';
import AppRoute from 'general/constants/AppRoute';
import ChargeLimitCard from '../ChargeLimitCard';

ChargeDriverDetailView.propTypes = {
  driverId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

function ChargeDriverDetailView({ driverId }) {
  // MARK: ---- PROPERTIES ----
  const router = useRouter();
  const { startEkyc } = useEkyc();
  const { user, permissionList } = useSelector((state) => state.auth);
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'OPERATOR';
  const [driverDetail, setDriverDetail] = useState(null);
  useEffect(() => {
    if (driverId) {
      ChargeHelper.API.Driver.requestGetDriverDetail(driverId).then((response) => {
        if (_.isObject(response)) setDriverDetail(response);
      });
    }
  }, [driverId]);
  const { operatorList } = useAgency();
  const tabs = useMemo(() => {
    return ['Lịch sử sạc xe', 'Lịch sử giao dịch'].reverse();
  }, []);
  const [selectedTab, setSelectedTab] = useState(tabs[1]);
  const [discountType, setDiscountType] = useState('');
  useEffect(() => {
    if (driverDetail?.discountRate === 0) setDiscountType('STANDARD');
    else if (driverDetail?.discountRate === 100) setDiscountType('FREE');
    else setDiscountType('DISCOUNT');
  }, [driverDetail])

  // MARK: ---- METHODS ----
  const handleSelectedTab = (newTab) => {
    if (selectedTab === newTab) return;
    setSelectedTab(newTab);
  };

  const handleRefresh = () => {
    if (driverId) {
      ChargeHelper.API.Driver.requestGetDriverDetail(driverId).then((response) => {
        if (_.isObject(response)) setDriverDetail(response);
      });
    }
  };
  const buildMoreActions = (row) => {
    const actions = [];
    if (isAdmin) {
      if (Utils.checkPermission(permissionList, AppConstants.permissionCode.driverUpdate.code)) {
        if (row?.status === ChargeConstants.ChargeDriverStatus.REGISTERED) {
          actions.push({
            text: 'Kích hoạt tài khoản',
            iconElement: <i className="fa-regular fa-bolt" style={{ fontSize: '1.1rem' }} />,
            click: () => {
              ChargeHelper.UI.Driver.showDialogConfirmActivateDriver(row, () => {
                handleRefresh();
              });
            },
          });
        }
        actions.push({
          text: 'Chỉnh sửa',
          iconElement: <i className="fa-regular fa-edit" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            router.navigate(`edit`);
          },
        });
        if (row?.status === ChargeConstants.ChargeDriverStatus.NORMAL) {
          actions.push({
            text: 'Nạp tiền cho khách hàng',
            iconElement: <i className="fa-regular fa-square-dollar" style={{ fontSize: '1.1rem' }} />,
            click: () => {
              ChargeHelper.UI.Driver.showDialogTopUpDriver(row, () => {
                handleRefresh();
              });
            },
          });
        }
        actions.push({
          text: 'Đặt giá sạc xe',
          iconElement: <i className="fa-regular fa-money-check-dollar-pen" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            ChargeHelper.UI.Driver.showDialogSetChargingPrice(row, () => {
              handleRefresh();
            });
          },
        });
        if (row?.status === ChargeConstants.ChargeDriverStatus.NORMAL) {
          actions.push({
            text: 'Ngừng hoạt động',
            iconElement: <i className="fa-regular fa-stop" style={{ fontSize: '1.1rem' }} />,
            click: () => {
              ChargeHelper.UI.Driver.showDialogConfirmSuspendDriver(row, () => {
                handleRefresh();
              });
            },
          });
        }
        if (row?.status === ChargeConstants.ChargeDriverStatus.SUSPENDED) {
          actions.push({
            text: 'Khôi phục hoạt động',
            iconElement: <i className="fa-regular fa-play" style={{ fontSize: '1.1rem' }} />,
            click: () => {
              ChargeHelper.UI.Driver.showDialogConfirmResumeDriver(row, () => {
                handleRefresh();
              });
            },
          });
        }
        actions.push({
          text: 'Đặt lại mật khẩu',
          iconElement: <i className="fa-regular fa-key" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            ChargeHelper.UI.Driver.showDialogChangePasswordDriver(row, () => {
              handleRefresh();
            });
          },
        });
      }
      if (Utils.checkPermission(permissionList, AppConstants.permissionCode.driverDelete.code)) {
        actions.push({
          text: 'Xóa khách hàng',
          iconElement: <i className="fa-regular fa-trash-can" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            ChargeHelper.UI.Driver.showDialogConfirmDeleteDriver(row, () => {
              handleRefresh();
            });
          },
        });
      }
    }
    return actions;
  };
  const handleAddVehicle = () => {
    AppCustomDialogHelper.show(
      'Thêm xe',
      <DialogAddVehicleView
        initialDriverId={driverId}
        onCancel={() => AppCustomDialogHelper.hide()}
        onDone={() => {
          AppCustomDialogHelper.hide();
          handleRefresh();
        }}
      />,
      'lg',
      false,
      true
    );
  };

  return (
    <div>
      {/* breadcrumb */}
      <div>
        {UIHelper.buildBreadcrumb([
          {
            title: 'Danh sách khách hàng',
            onPress: () => {
              router.navigate(-1);
            },
          },
          { title: 'Chi tiết khách hàng' },
        ])}
      </div>

      <div className="row m-0 p-0 px-2 mt-4">
        {/* thong tin khach hang */}
        <div
          className={`${driverDetail?.status === ChargeConstants.ChargeDriverStatus.NORMAL
            ? 'col-xl-9 col-lg-8'
            : 'col-12'
            } mb-4 px-2`}
        >
          {UIHelper.buildCard(
            <div>
              {/* header */}
              <div
                className="d-flex align-items-start justify-content-between px-4 py-3"
                style={{
                  borderBottom: `1px dashed ${AppColors.gray[300]}`,
                }}
              >
                <div className="flex-fill">
                  <div className="d-flex align-items-center gap-3">
                    {/* avatar */}
                    <div className="">
                      {UIHelper.buildAvatar(
                        driverDetail?.avatar ? Utils.getFullUrl(driverDetail?.avatar) : null,
                        44,
                        AppResource.icons.avatar.default
                      )}
                    </div>
                    <div className="d-flex flex-column">
                      <div className="d-flex align-items-start gap-2">
                        <span style={UIHelper.buildFontStyle('600', '18px', AppColors.gray[800])}>
                          {driverDetail?.fullname?.toUpperCase()}
                        </span>
                        <span className="min-w-100px pt-1">
                          <KTLabel
                            text={ChargeHelper.Utils.getDriverStatusName(driverDetail?.status)}
                            isInline={true}
                            size={KTLabelSize.medium}
                            additionalClassName={`label-light-${ChargeHelper.Utils.getDriverStatusColor(
                              driverDetail?.status
                            )}`}
                          />
                        </span>
                      </div>
                      <span style={UIHelper.buildFontStyle('400', '14px', AppColors.gray[700])}>
                        Ngày tạo:{' '}
                        <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
                          {Utils.formatDateTimeToString(
                            driverDetail?.createdAt,
                            'DD/MM/YYYY - HH:mm'
                          )}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-3">
                  {UIHelper.buildMoreActionsButton(buildMoreActions(driverDetail))}
                </div>
              </div>

              <div className="row m-0 p-0 px-2 mt-4">
                {/* thong tin tai khoan */}
                <div className="col-lg-6 mb-4 px-2">
                  <div>
                    {UIHelper.buildSectionTitle('Thông tin tài khoản')}
                    {UIHelper.buildSectionCard(
                      <div>
                        {/* {UIHelper.buildCardCellItem(
                          'Nhà điều hành',
                          _.find(
                            operatorList,
                            (item) => item.operatorId === driverDetail?.operatorId
                          )?.name,
                          () => { }
                        )} */}
                        {UIHelper.buildCardCellItem('Họ và tên', driverDetail?.fullname)}
                        {UIHelper.buildCardCellItem('Email', driverDetail?.email)}
                        {UIHelper.buildCardCellItem('Số điện thoại', driverDetail?.phone)}
                        {UIHelper.buildCardCellItem('Loại khách hàng', FleetHelper.Utils.getFleetDriverTypeName(driverDetail?.driver?.type))}
                        {UIHelper.buildCardCellItem('Doanh nghiệp quản lý', driverDetail?.driver?.agency?.name, () => Utils.openInNewTab(`${AppRoute.fleetList}/${driverDetail?.driver?.agencyId ?? ''}`))}
                        {UIHelper.buildCardCellItem('Số CCCD', driverDetail?.idCardNumber)}
                        {UIHelper.buildCardCellItem('Hình ảnh CCCD', driverDetail?.idCardDocuments?.length
                          ? `Xem tệp đính kèm (${driverDetail?.idCardDocuments?.length})`
                          : '',
                          () => {
                            if (driverDetail?.idCardDocuments?.length)
                              DealerHelper.handleShowFile(driverDetail?.idCardDocuments);
                          })}
                        {UIHelper.buildCardCellItem('Loại giá sạc', ChargeHelper.Utils.getDriverPricingTypeName(discountType), null, false)}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-lg-6 mb-4 px-2">
                  {/* thong tin dinh danh eKYC */}
                  <div>
                    {UIHelper.buildSectionTitle('Thông tin định danh eKYC')}
                    {UIHelper.buildSectionCard(
                      <div>
                        {UIHelper.buildCardCellItem(
                          'Trạng thái',
                          <KTLabel
                            text={
                              driverDetail?.ekycVerified
                                ? 'Đã xác thực'
                                : 'Chưa xác thực'
                            }
                            isInline={true}
                            size={KTLabelSize.medium}
                            additionalClassName={`label-light-${driverDetail?.ekycVerified ? 'success' : 'danger'}`}
                          />,
                          // _.isBoolean(driverDetail?.ekycVerified) && driverDetail?.ekycVerified
                          //   ? 'Đã xác thực'
                          //   : 'Chưa xác thực',
                          null,
                          _.isBoolean(driverDetail?.ekycVerified) && driverDetail?.ekycVerified
                        )}
                        {driverDetail?.ekycVerified ? (
                          <div className="d-flex justify-content-between pt-3 mx-0 mb-3 px-3">
                            <div
                              className="w-120px font-weight-bold"
                              style={{
                                color: AppColors.gray[600],
                                fontWeight: '400',
                                fontSize: Utils.returnFontSize(14),
                              }}
                            >
                              Dữ liệu xác thực
                            </div>
                            <div
                              className="font-weight-bolder cursor-pointer"
                              style={{
                                color: AppColors.primary,
                                fontWeight: '600',
                                fontSize: Utils.returnFontSize(14),
                              }}
                              onClick={() => {
                                if (driverDetail?.idCardDocuments?.length)
                                  DealerHelper.handleShowFile(driverDetail?.idCardDocuments);
                              }}
                            >
                              {driverDetail?.idCardDocuments?.length
                                ? `Xem tệp đính kèm (${driverDetail?.idCardDocuments?.length})`
                                : ''}
                            </div>
                          </div>
                        ) : (
                          <div className="px-3 mb-3">
                            <KTBSButton
                              iconElement={
                                <i className="fa-regular fa-shield-check pr-0" style={{ fontSize: 15 }} />
                              }
                              color={KTBSButtonColor.secondary}
                              title="Xác thực"
                              size={KTBSButtonSize.large}
                              borderRadius={5}
                              buttonTitleFontWeight="600"
                              buttonTitleFontSize={13}
                              onPress={() => {
                                startEkyc(`Driver|${driverDetail?.userId}`, () => handleRefresh());
                              }}
                              buttonTitleColor={AppColors.gray[800]}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* thong tin xuat hoa don cong ty */}
                  {driverDetail?.driver?.type === FleetConstants.FleetDriverType.PERSONAL && (
                    <div className='mt-4'>
                      {UIHelper.buildSectionTitle('Thông tin xuất hóa đơn công ty')}
                      {UIHelper.buildSectionCard(
                        <div>
                          {UIHelper.buildCardCellItem('Tên công ty', driverDetail?.driver?.companyName)}
                          {UIHelper.buildCardCellItem('MST/Mã số định danh', driverDetail?.driver?.taxCode)}
                          {UIHelper.buildCardCellItem('Địa chỉ', driverDetail?.driver?.address, null, false)}
                        </div>
                      )}
                    </div>
                  )}

                  {/* thong tin khac */}
                  {driverDetail?.driver?.type === FleetConstants.FleetDriverType.ORGANIZATION && (
                    <div className='mt-4'>
                      {UIHelper.buildSectionTitle('Thông tin khác')}
                      {UIHelper.buildSectionCard(
                        <div>
                          {UIHelper.buildCardCellItem('Mã tài xế của doanh nghiệp', driverDetail?.driver?.companyCode)}
                          {UIHelper.buildCardCellItem('Hình thức làm việc', FleetHelper.Utils.getFleetDriverWorkingTypeName(driverDetail?.driver?.workingType))}
                          {UIHelper.buildCardCellItem('Số lượng xe cho phép', driverDetail?.driver?.carNumbers?.toString())}
                          {UIHelper.buildCardCellItem('Chính sách thanh toán phí sạc', FleetHelper.Utils.getPaymentMethodName(driverDetail?.driver?.paymentMethod), null, false)}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        {/* thong tin vi */}
        {driverDetail?.status === ChargeConstants.ChargeDriverStatus.NORMAL && (
          <div className="col-xl-3 col-lg-4 mb-4 px-2 d-flex flex-column gap-3">
            <WalletCard
              amount={_.toNumber(_.first(driverDetail?.wallets)?.balance)}
              currency={_.first(driverDetail?.wallets)?.currency}
              onPressedTopUp={() => {
                ChargeHelper.UI.Driver.showDialogTopUpDriver(driverDetail, () => {
                  handleRefresh();
                });
              }}
            />
            <ChargeLimitCard
              value={0}
              maxValue={driverDetail?.driver?.creditLimit ?? 0}
              completed={driverDetail?.driver?.creditUsedAmount ?? 0}
              bgColor='#32ADE6'
              baseBgColor='#DBDFF1'
              period={driverDetail?.driver?.creditLimitPeriod ?? 'MONTH'}
            />
          </div>
        )}
      </div>

      {/* danh sach xe */}
      <div className="px-4">
        {UIHelper.buildCard(
          <div>
            {/* header */}
            <div className="px-4 pt-4 pb-4 d-flex align-items-center justify-content-between">
              {/* title */}
              <div className="d-flex flex-column">
                <span style={UIHelper.buildFontStyle('600', '18px', AppColors.gray[800])}>
                  Danh sách xe
                </span>
                <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
                  Tổng:{' '}
                  <span style={UIHelper.buildFontStyle('600', '14px', AppColors.primary)}>
                    {driverDetail?.vehicles?.length} xe
                  </span>
                </span>
              </div>

              {/* actions */}
              {isAdmin && Utils.checkPermission(permissionList, AppConstants.permissionCode.driverUpdate.code) &&
                UIHelper.buildGreenButton(
                  'Thêm xe',
                  handleAddVehicle,
                  <i className="fa-regular fa-circle-plus" />
                )}
            </div>
            <div className="row m-0 p-0 px-2">
              {driverDetail?.vehicles?.map((item, index) => (
                <div
                  key={`VEHICLE_${item?.vehicleId ?? index}`}
                  className="col-lg-4 col-md-6 mb-4 px-2"
                >
                  <CardVehicleView
                    vehicle={item}
                    onRefresh={() => {
                      handleRefresh();
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* lich su thanh toan & lich su giao dich */}
      <div className="px-4">
        <AppTabs tabs={tabs} activeTab={selectedTab} handleClick={handleSelectedTab} />
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedTab}
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -10, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div>
              {selectedTab === tabs[1] ? (
                <div>
                  {/* lich su sac xe */}
                  <DriverPaymentHistoryView hasAppTab={true} driverId={driverId} driverDetail={driverDetail} />
                </div>
              ) : (
                <div>
                  {/* lich su giao dich */}
                  <DriverTopUpHistoryView hasAppTab={true} driverId={driverId} driverDetail={driverDetail} />
                </div>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

export default ChargeDriverDetailView;
